import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  InvolveGuestAsSharedAssessmentReaderRequestSchema,
  professionalRelations,
  sharedGuestEndsOnByDefault,
  type AssessmentId,
  type EarlierGuest,
  type EmailAddress,
  type FinishedAssessment,
  type InvolveGuestAsSharedAssessmentReaderRequest,
  type ProfessionalRelation,
  type UserId,
} from "@thalamos/haven-common";

import {
  calendarDayText,
  type EarlierGuestsMatch,
} from "../AskGuestDialog/AskGuestDialog.js";
import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** How many assessments the first step lists before Show older. */
const LISTED_AT_FIRST = 5;

/** How many chosen assessments the second step names before "and n more". */
const NAMED_AT_FIRST = 3;

/** One finished assessment the sharer may read, as the dialog offers it. */
export type ShareableAssessment = {
  assessment: FinishedAssessment;
  assessorName: string;
};

/**
 * How sharing went: shared, refused for the address in the server's words,
 * refused because the guest can already read this person's shared
 * assessments or the record has no date of birth, or not reached.
 */
export type ShareWithGuestOutcome =
  | { kind: "shared" }
  | { kind: "address_refused"; message: string }
  | { kind: "already_shared" }
  | { kind: "no_date_of_birth" }
  | { kind: "failed" };

type Field = "name" | "email" | "relationDescription" | "jobTitle" | "endsOn";

/**
 * Where sharing stands: choosing what to share, saying who and for how long,
 * or saying whether an earlier guest is the one shared with.
 */
type Step =
  | { kind: "choose" }
  | { kind: "who" }
  | {
      kind: "earlier_guest";
      request: InvolveGuestAsSharedAssessmentReaderRequest;
      earlierGuests: readonly EarlierGuest[];
    };

/** The value of the radio that says the guest is none of the earlier guests. */
const SOMEBODY_NEW = "somebody_new";

/** What a finished assessment is, in the dialog's words: "Definitive, approved 2 May 2026". */
const useAssessmentWords = () => {
  const { t } = useTranslation();

  return ({ assessment, assessorName }: ShareableAssessment): string =>
    t("shareWithGuest.choose.assessment", {
      kind: assessment.definitive
        ? t("shareWithGuest.choose.definitive")
        : t("shareWithGuest.choose.nonDefinitive"),
      finished: t(
        `personPage.assessments.history.finished.${assessment.status}`,
        { date: CALENDAR_DATE.format(assessment.finishedAt) },
      ),
      assessor: assessorName,
    });
};

/**
 * Shares finished assessments of one person, read only, with somebody
 * outside the service, in two steps: which assessments, newest first, then
 * who they are and the day their access ends, three months from today unless
 * another is chosen. The warning is ticked afresh every time the dialog
 * opens.
 *
 * Where the address reached a guest before, on a person the sharer reaches,
 * the sharer says whether this is the guest they mean before sharing.
 */
export const ShareWithGuestDialog = ({
  open,
  personName,
  assessments,
  onMatch,
  onShare,
  onClose,
}: {
  open: boolean;
  /** Who the assessments are about, as the dialog names them. */
  personName: string;
  /** The finished assessments the sharer may read, newest first. */
  assessments: readonly ShareableAssessment[];
  onMatch: (email: EmailAddress) => Promisable<EarlierGuestsMatch>;
  onShare: (
    request: InvolveGuestAsSharedAssessmentReaderRequest,
  ) => Promisable<ShareWithGuestOutcome>;
  onClose: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const wordsOf = useAssessmentWords();
  const [step, setStep] = useState<Step>({ kind: "choose" });
  const [chosen, setChosen] = useState<ReadonlySet<AssessmentId>>(new Set());
  const [showingOlder, setShowingOlder] = useState(false);
  const [showingAllChosen, setShowingAllChosen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [relation, setRelation] = useState<ProfessionalRelation | "">("");
  const [relationDescription, setRelationDescription] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [defaultEnd] = useState(() => sharedGuestEndsOnByDefault());
  const [endChoice, setEndChoice] = useState<"default" | "chosen">("default");
  const [chosenDay, setChosenDay] = useState("");
  const [warned, setWarned] = useState(false);
  const [triedToSend, setTriedToSend] = useState(false);
  const [earlierGuestUserId, setEarlierGuestUserId] = useState<
    UserId | typeof SOMEBODY_NEW | ""
  >("");
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<ShareWithGuestOutcome>();

  // Newest first, as offered, whatever order they were ticked in.
  const chosenInOrder = assessments.filter(({ assessment }) =>
    chosen.has(assessment.id),
  );
  const listed = showingOlder
    ? assessments
    : assessments.slice(0, LISTED_AT_FIRST);
  const allChosen =
    assessments.length > 0 && chosenInOrder.length === assessments.length;

  const parsed = InvolveGuestAsSharedAssessmentReaderRequestSchema.safeParse({
    assessmentIds: chosenInOrder.map(({ assessment }) => assessment.id),
    name,
    email: email.trim(),
    earlierGuestUserId: null,
    relation: {
      relationGroup: "professional",
      relation,
      relationDescription: relation === "other" ? relationDescription : null,
    },
    jobTitle,
    endsOn: endChoice === "default" ? defaultEnd : chosenDay,
  });

  // The schema's messages are translation keys; an address it refuses says
  // so in words of the dialog's own.
  const problemWith = (field: Field): string | undefined => {
    const issue = parsed.error?.issues.find((each) =>
      field === "relationDescription"
        ? each.path[0] === "relation" && each.path[1] === field
        : each.path[0] === field,
    );

    if (!triedToSend || issue === undefined) {
      return undefined;
    }

    return field === "email"
      ? t("askGuest.emailInvalid")
      : t(issue.message, { defaultValue: t("askGuest.invalid") });
  };
  const relationMissing = triedToSend && relation === "";
  const warningMissing = triedToSend && !warned;
  const addressRefusal =
    outcome?.kind === "address_refused" ? outcome.message : undefined;

  const share = async (
    request: InvolveGuestAsSharedAssessmentReaderRequest,
  ) => {
    setOutcome(undefined);
    setSending(true);

    try {
      const shared = await onShare(request);
      setOutcome(shared);
      if (shared.kind === "address_refused") {
        setStep({ kind: "who" });
      }
    } catch {
      setOutcome({ kind: "failed" });
    } finally {
      setSending(false);
    }
  };

  const sendDetails = async () => {
    setTriedToSend(true);

    if (!parsed.success || !warned) {
      return;
    }

    setOutcome(undefined);
    setSending(true);

    let matched: EarlierGuestsMatch;
    try {
      matched = await onMatch(parsed.data.email);
    } catch {
      matched = { kind: "failed" };
    }
    setSending(false);

    if (matched.kind === "failed") {
      setOutcome({ kind: "failed" });
      return;
    }

    if (matched.earlierGuests.length > 0) {
      setEarlierGuestUserId("");
      setStep({
        kind: "earlier_guest",
        request: parsed.data,
        earlierGuests: matched.earlierGuests,
      });
      return;
    }

    await share(parsed.data);
  };

  const failure =
    outcome?.kind === "already_shared" ||
    outcome?.kind === "no_date_of_birth" ||
    outcome?.kind === "failed" ? (
      <Alert severity="error">
        {t(`shareWithGuest.outcome.${outcome.kind}`, { personName })}
      </Alert>
    ) : null;

  const oldest = chosenInOrder.at(-1)?.assessment.finishedAt;
  const newest = chosenInOrder[0]?.assessment.finishedAt;
  const range =
    oldest === undefined || newest === undefined
      ? ""
      : CALENDAR_DATE.format(oldest) === CALENDAR_DATE.format(newest)
        ? t("shareWithGuest.who.onOneDay", {
            date: CALENDAR_DATE.format(newest),
          })
        : t("shareWithGuest.who.range", {
            from: CALENDAR_DATE.format(oldest),
            to: CALENDAR_DATE.format(newest),
          });
  const named = showingAllChosen
    ? chosenInOrder
    : chosenInOrder.slice(0, NAMED_AT_FIRST);

  const content = () => {
    if (step.kind === "choose") {
      return (
        <>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DialogContentText>
                {t("shareWithGuest.choose.intro", { personName })}
              </DialogContentText>
              {assessments.length === 0 ? (
                <Alert severity="info">{t("shareWithGuest.choose.none")}</Alert>
              ) : (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allChosen}
                        indeterminate={chosen.size > 0 && !allChosen}
                        onChange={() => {
                          setChosen(
                            allChosen
                              ? new Set()
                              : new Set(
                                  assessments.map(
                                    ({ assessment }) => assessment.id,
                                  ),
                                ),
                          );
                        }}
                      />
                    }
                    label={t("shareWithGuest.choose.selectAll")}
                  />
                  <Box
                    sx={
                      showingOlder ? { maxHeight: 320, overflowY: "auto" } : {}
                    }
                  >
                    {listed.map((offered) => (
                      <Box key={offered.assessment.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={chosen.has(offered.assessment.id)}
                              onChange={() => {
                                const next = new Set(chosen);
                                if (next.has(offered.assessment.id)) {
                                  next.delete(offered.assessment.id);
                                } else {
                                  next.add(offered.assessment.id);
                                }
                                setChosen(next);
                              }}
                            />
                          }
                          label={wordsOf(offered)}
                        />
                      </Box>
                    ))}
                  </Box>
                  {assessments.length > LISTED_AT_FIRST ? (
                    <Button
                      size="small"
                      onClick={() => {
                        setShowingOlder(!showingOlder);
                      }}
                    >
                      {showingOlder
                        ? t("shareWithGuest.choose.showFewer")
                        : t("shareWithGuest.choose.showOlder")}
                    </Button>
                  ) : null}
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Typography
              variant="body2"
              color="text.secondary"
              aria-live="polite"
              sx={{ mr: "auto", pl: 2 }}
            >
              {t("shareWithGuest.choose.chosen", { count: chosen.size })}
            </Typography>
            <Button
              disabled={chosen.size === 0}
              onClick={() => {
                setChosen(new Set());
              }}
            >
              {t("shareWithGuest.choose.clear")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                void onClose();
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={chosen.size === 0}
              onClick={() => {
                setShowingAllChosen(false);
                setStep({ kind: "who" });
              }}
            >
              {t("shareWithGuest.choose.next")}
            </Button>
          </DialogActions>
        </>
      );
    }

    if (step.kind === "earlier_guest") {
      return (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel id="share-guest-earlier">
                  {t("shareWithGuest.earlierGuest.question", {
                    email: step.request.email,
                  })}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="share-guest-earlier"
                  value={earlierGuestUserId}
                  onChange={(event) => {
                    const picked = step.earlierGuests.find(
                      (guest) => guest.userId === event.target.value,
                    );
                    setEarlierGuestUserId(picked?.userId ?? SOMEBODY_NEW);
                  }}
                >
                  {step.earlierGuests.map((guest) => (
                    <FormControlLabel
                      key={guest.userId}
                      value={guest.userId}
                      control={<Radio />}
                      disabled={sending}
                      label={
                        guest.jobTitle === null
                          ? guest.name
                          : t("askGuest.earlierGuest.withJobTitle", {
                              name: guest.name,
                              jobTitle: guest.jobTitle,
                            })
                      }
                    />
                  ))}
                  <FormControlLabel
                    value={SOMEBODY_NEW}
                    control={<Radio />}
                    disabled={sending}
                    label={t("askGuest.earlierGuest.somebodyNew", {
                      name: step.request.name,
                    })}
                  />
                </RadioGroup>
              </FormControl>
              {failure}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              disabled={sending}
              onClick={() => {
                setOutcome(undefined);
                setStep({ kind: "who" });
              }}
            >
              {t("askGuest.back")}
            </Button>
            <Button
              variant="contained"
              disabled={sending || earlierGuestUserId === ""}
              onClick={() => {
                void share({
                  ...step.request,
                  earlierGuestUserId:
                    earlierGuestUserId === SOMEBODY_NEW ||
                    earlierGuestUserId === ""
                      ? null
                      : earlierGuestUserId,
                });
              }}
            >
              {t("shareWithGuest.who.send", {
                count: step.request.assessmentIds.length,
              })}
            </Button>
          </DialogActions>
        </>
      );
    }

    return (
      <>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="subtitle2" component="h3">
                  {t("shareWithGuest.who.sharing", {
                    count: chosenInOrder.length,
                    range,
                  })}
                </Typography>
                <Button
                  size="small"
                  disabled={sending}
                  onClick={() => {
                    setOutcome(undefined);
                    setStep({ kind: "choose" });
                  }}
                >
                  {t("shareWithGuest.who.change")}
                </Button>
              </Stack>
              <List dense disablePadding>
                {named.map((each) => (
                  <ListItem key={each.assessment.id} disableGutters>
                    {wordsOf(each)}
                  </ListItem>
                ))}
              </List>
              {chosenInOrder.length > named.length ? (
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="body2">
                    {t("shareWithGuest.who.andMore", {
                      count: chosenInOrder.length - named.length,
                    })}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowingAllChosen(true);
                    }}
                  >
                    {t("shareWithGuest.who.showAll")}
                  </Button>
                </Stack>
              ) : null}
            </Box>
            <TextField
              label={t("askGuest.name")}
              value={name}
              disabled={sending}
              error={problemWith("name") !== undefined}
              helperText={problemWith("name")}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <TextField
              label={t("askGuest.email")}
              type="email"
              value={email}
              disabled={sending}
              error={
                problemWith("email") !== undefined ||
                addressRefusal !== undefined
              }
              helperText={problemWith("email") ?? addressRefusal}
              onChange={(event) => {
                setEmail(event.target.value);
                setOutcome(undefined);
              }}
            />
            <TextField
              select
              label={t("askGuest.relation")}
              value={relation}
              disabled={sending}
              error={relationMissing}
              helperText={
                relationMissing ? t("askGuest.relationRequired") : undefined
              }
              onChange={(event) => {
                const picked = professionalRelations.find(
                  (each) => each === event.target.value,
                );
                setRelation(picked ?? "");
              }}
            >
              {/* Guests are professionals reached at a work address, so the
                  family and carer groups are not offered. */}
              {professionalRelations.map((each) => (
                <MenuItem key={each} value={each}>
                  {t(`standing.relation.professional.${each}`)}
                </MenuItem>
              ))}
            </TextField>
            {relation === "other" ? (
              <TextField
                label={t("askGuest.relationDescription")}
                value={relationDescription}
                disabled={sending}
                error={problemWith("relationDescription") !== undefined}
                helperText={problemWith("relationDescription")}
                onChange={(event) => {
                  setRelationDescription(event.target.value);
                }}
              />
            ) : null}
            <TextField
              label={t("askGuest.jobTitle")}
              value={jobTitle}
              disabled={sending}
              error={problemWith("jobTitle") !== undefined}
              helperText={problemWith("jobTitle")}
              onChange={(event) => {
                setJobTitle(event.target.value);
              }}
            />
            <FormControl>
              <FormLabel id="share-guest-ends">
                {t("shareWithGuest.who.endsOn")}
              </FormLabel>
              <RadioGroup
                aria-labelledby="share-guest-ends"
                value={endChoice}
                onChange={(event) => {
                  setEndChoice(
                    event.target.value === "chosen" ? "chosen" : "default",
                  );
                }}
              >
                <FormControlLabel
                  value="default"
                  control={<Radio />}
                  disabled={sending}
                  label={t("shareWithGuest.who.endsAfterThreeMonths", {
                    date: calendarDayText(defaultEnd),
                  })}
                />
                <FormControlLabel
                  value="chosen"
                  control={<Radio />}
                  disabled={sending}
                  label={t("shareWithGuest.who.endsOnChosenDay")}
                />
              </RadioGroup>
            </FormControl>
            {endChoice === "chosen" ? (
              <TextField
                type="date"
                label={t("askGuest.chosenDay")}
                value={chosenDay}
                disabled={sending}
                error={problemWith("endsOn") !== undefined}
                helperText={
                  problemWith("endsOn") ?? t("shareWithGuest.who.chosenDayHelp")
                }
                onChange={(event) => {
                  setChosenDay(event.target.value);
                }}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ maxWidth: 240 }}
              />
            ) : null}
            <FormControl error={warningMissing}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={warned}
                    disabled={sending}
                    onChange={(event) => {
                      setWarned(event.target.checked);
                    }}
                  />
                }
                label={t("shareWithGuest.who.warning", { personName })}
              />
              {warningMissing ? (
                <FormHelperText>
                  {t("shareWithGuest.who.warningRequired")}
                </FormHelperText>
              ) : null}
            </FormControl>
            {failure}
          </Stack>
        </DialogContent>
        <CancelOrProceedActions
          cancel={{
            onClick: onClose,
            disabled: sending,
          }}
          proceed={{
            label: t("shareWithGuest.who.send", {
              count: chosenInOrder.length,
            }),
            onClick: sendDetails,
            disabled: sending,
            kind: "primary",
          }}
        />
      </>
    );
  };

  return (
    <Dialog
      open={open}
      // Nothing closes the dialog while a request is on its way, so its
      // outcome is never lost.
      onClose={
        sending
          ? undefined
          : () => {
              void onClose();
            }
      }
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("shareWithGuest.title")}</DialogTitle>
      {content()}
    </Dialog>
  );
};
